import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";

interface Props {
  threadId: string | undefined;
  onCancel: () => void;
  onDeleted: (threadId: string) => void;
}

const deleteAssistantChat = withErrorHandling(adminApi.deleteAssistantChat);

const DeleteChatDialog = ({ threadId, onCancel, onDeleted }: Props) => {
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    if (deleting) {
      return;
    }
    onCancel();
    setErrorMessage("");
  };

  const handleDelete = async () => {
    if (!threadId) {
      return;
    }

    setDeleting(true);
    const [, error] = await deleteAssistantChat(threadId);
    setDeleting(false);
    if (error) {
      logError(error, "[DeleteChatDialog] deleteAssistantChat");
      setErrorMessage(error.message);
      return;
    }

    setErrorMessage("");
    onDeleted(threadId);
  };

  return (
    <SmallDialog open={Boolean(threadId)} onClose={handleClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center", textAlign: "center" }}>
            <Typography variant="h6">Delete this chat?</Typography>
            <Typography color="textSecondary">
              The chat and its files will be deleted. This can&apos;t be undone.
            </Typography>
            {errorMessage && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={deleting}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteChatDialog;
